import { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Switch,
  Button,
  Space,
} from 'antd';
import { Container, NaverMap, Marker, Listener, useNavermaps } from 'react-naver-maps';
import { useCreatePin, useUpdatePin } from '@/hooks/usePins';
import { PIN_LEVEL_CONFIG, SPORT_TYPE_CONFIG } from '@/config/constants';
import type { Pin } from '@/types/pin';
import type { SportType } from '@/types/user';

interface PinFormModalProps {
  open: boolean;
  pin: Pin | null;
  onClose: () => void;
  initialCoords?: { lat: number; lng: number };
}

interface PinFormValues {
  name: string;
  level: string;
  lat: number;
  lng: number;
  isActive: boolean;
  allowedSports?: string[];
  minAge?: number | null;
  maxAge?: number | null;
}

const SEOUL_CENTER: [number, number] = [37.5665, 126.978];

export function PinFormModal({ open, pin, onClose, initialCoords }: PinFormModalProps) {
  const [form] = Form.useForm<PinFormValues>();
  const navermaps = useNavermaps();
  const isEdit = !!pin;

  const createMutation = useCreatePin();
  const updateMutation = useUpdatePin();

  const isPending = createMutation.isPending || updateMutation.isPending;

  // 폼 필드에서 현재 lat/lng 읽기 (지도 마커 위치용)
  const latValue = Form.useWatch('lat', form);
  const lngValue = Form.useWatch('lng', form);

  const hasCoords =
    typeof latValue === 'number' &&
    typeof lngValue === 'number' &&
    !isNaN(latValue) &&
    !isNaN(lngValue);

  const markerPosition: [number, number] | null = hasCoords ? [latValue, lngValue] : null;

  const mapCenter: [number, number] = markerPosition ?? SEOUL_CENTER;

  useEffect(() => {
    if (open) {
      if (pin) {
        form.setFieldsValue({
          name: pin.name,
          level: pin.level,
          lat: pin.center.lat,
          lng: pin.center.lng,
          isActive: pin.isActive,
          allowedSports: pin.allowedSports ?? [],
          minAge: pin.minAge ?? null,
          maxAge: pin.maxAge ?? null,
        });
      } else if (initialCoords) {
        form.resetFields();
        form.setFieldsValue({
          lat: initialCoords.lat,
          lng: initialCoords.lng,
          isActive: true,
        });
      } else {
        form.resetFields();
      }
    }
  }, [open, pin, initialCoords, form]);

  const handleMapClick = (lat: number, lng: number) => {
    form.setFieldsValue({ lat: parseFloat(lat.toFixed(6)), lng: parseFloat(lng.toFixed(6)) });
  };

  const handleDragEnd = (lat: number, lng: number) => {
    form.setFieldsValue({ lat: parseFloat(lat.toFixed(6)), lng: parseFloat(lng.toFixed(6)) });
  };

  const handleSubmit = async (values: PinFormValues) => {
    const payload = {
      name: values.name,
      level: values.level,
      center: { lat: values.lat, lng: values.lng },
      allowedSports:
        values.allowedSports && values.allowedSports.length > 0
          ? (values.allowedSports as SportType[])
          : null,
      minAge: values.minAge ?? null,
      maxAge: values.maxAge ?? null,
    };

    if (isEdit) {
      await updateMutation.mutateAsync({
        id: pin!.id,
        data: { ...payload, isActive: values.isActive },
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      title={isEdit ? `핀 수정 — ${pin?.name}` : '핀 생성'}
      onOk={() => form.submit()}
      onCancel={onClose}
      okText={isEdit ? '수정' : '생성'}
      cancelText="취소"
      confirmLoading={isPending}
      destroyOnHide
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isActive: true }}
      >
        <Form.Item
          name="name"
          label="핀 이름"
          rules={[{ required: true, message: '핀 이름을 입력해주세요.' }]}
        >
          <Input placeholder="예: 강남구 역삼동" />
        </Form.Item>

        <Form.Item
          name="level"
          label="레벨"
          rules={[{ required: true, message: '레벨을 선택해주세요.' }]}
        >
          <Select placeholder="레벨 선택">
            {Object.entries(PIN_LEVEL_CONFIG).map(([value, { label, activationThreshold }]) => (
              <Select.Option key={value} value={value}>
                {label} (활성화 임계값: {activationThreshold}명)
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="lat"
              label="위도 (Latitude)"
              rules={[
                { required: true, message: '위도를 입력해주세요.' },
                {
                  type: 'number',
                  min: 33,
                  max: 39,
                  message: '유효한 한국 위도를 입력하세요 (33~39)',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={6}
                placeholder="37.504049"
                onChange={(val) => {
                  if (typeof val === 'number') {
                    form.setFieldsValue({ lat: val });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lng"
              label="경도 (Longitude)"
              rules={[
                { required: true, message: '경도를 입력해주세요.' },
                {
                  type: 'number',
                  min: 124,
                  max: 132,
                  message: '유효한 한국 경도를 입력하세요 (124~132)',
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                precision={6}
                placeholder="127.024612"
                onChange={(val) => {
                  if (typeof val === 'number') {
                    form.setFieldsValue({ lng: val });
                  }
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 핀 접근 제약 — 허용 종목 / 연령 (비우면 전체 허용) */}
        <Form.Item
          name="allowedSports"
          label="허용 종목"
          tooltip="비워두면 모든 종목 허용. 선택하면 해당 종목만 이 핀에서 매칭/리그 가능"
        >
          <Select mode="multiple" allowClear placeholder="전체 허용 (제한하려면 종목 선택)">
            {Object.entries(SPORT_TYPE_CONFIG).map(([value, { label, icon }]) => (
              <Select.Option key={value} value={value}>
                {icon} {label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="연령 제한" tooltip="비워두면 제한 없음. 본인인증 생년월일로 검증됩니다.">
          <Space wrap style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => form.setFieldsValue({ minAge: 13, maxAge: 15 })}>
              중학생(13~15)
            </Button>
            <Button size="small" onClick={() => form.setFieldsValue({ minAge: 16, maxAge: 18 })}>
              고등학생(16~18)
            </Button>
            <Button size="small" onClick={() => form.setFieldsValue({ minAge: 19, maxAge: null })}>
              성인(19+)
            </Button>
            <Button size="small" onClick={() => form.setFieldsValue({ minAge: null, maxAge: null })}>
              제한 없음
            </Button>
          </Space>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="minAge" noStyle>
                <InputNumber style={{ width: '100%' }} min={5} max={100} placeholder="최소 나이" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxAge" noStyle>
                <InputNumber style={{ width: '100%' }} min={5} max={100} placeholder="최대 나이" />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>

        {isEdit && (
          <Form.Item
            name="isActive"
            label="활성 상태"
            valuePropName="checked"
          >
            <Switch checkedChildren="활성" unCheckedChildren="비활성" />
          </Form.Item>
        )}

        {/* 지도 — 클릭 또는 드래그로 좌표 입력 */}
        <Form.Item label="지도에서 위치 선택" style={{ marginBottom: 0 }}>
          <div style={{ height: 300, borderRadius: 8, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
            {open && (
              <Container style={{ height: '100%', width: '100%' }}>
                <NaverMap
                  center={new navermaps.LatLng(mapCenter[0], mapCenter[1])}
                  zoom={13}
                >
                  <Listener
                    type="click"
                    listener={(e: naver.maps.PointerEvent) =>
                      handleMapClick(e.coord.y, e.coord.x)
                    }
                  />
                  {markerPosition && (
                    <Marker
                      position={
                        new navermaps.LatLng(markerPosition[0], markerPosition[1])
                      }
                      draggable
                      onDragend={(e: naver.maps.PointerEvent) =>
                        handleDragEnd(e.coord.y, e.coord.x)
                      }
                    />
                  )}
                </NaverMap>
              </Container>
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
            지도를 클릭하거나 마커를 드래그하면 좌표가 자동 입력됩니다.
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
